import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { authClient } from "../../../lib/auth-client";
import { toast } from "react-toastify";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "../../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";

type InvitationState = "loading" | "success" | "error";

const AcceptInvitation = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [state, setState] = useState<InvitationState>("loading");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (!id) {
      setState("error");
      setErrorMessage("ID do convite não informado");
      return;
    }

    const acceptInvitation = async () => {
      try {
        const { error } = await authClient.organization.acceptInvitation({
          invitationId: id,
        });

        if (error) {
          setState("error");
          setErrorMessage(
            error.message || "Erro ao aceitar convite"
          );
        } else {
          setState("success");
          toast.success("Convite aceito com sucesso!");
          setTimeout(() => navigate("/campaign"), 2000);
        }
      } catch {
        setState("error");
        setErrorMessage("Erro ao aceitar convite. Verifique se você está autenticado.");
      }
    };

    acceptInvitation();
  }, [id, navigate]);

  document.title = "Aceitar convite | Ad Pulse";

  return (
    <React.Fragment>
      <div className="relative isolate min-h-screen bg-white text-gray-900">
        <div className="flex min-h-[calc(100vh-88px)] items-center justify-center px-4 pb-10">
          <Card className="w-full max-w-md rounded-2xl border border-gray-200 bg-white shadow-sm">
            <CardHeader className="space-y-1 pb-4 pt-7 text-center">
              <CardTitle className="text-xl font-bold text-gray-900 sm:text-3xl">
                Convite de organização
              </CardTitle>
              <CardDescription className="text-sm text-gray-500">
                {state === "loading" && "Processando seu convite..."}
                {state === "success" && "Seu convite foi aceito!"}
                {state === "error" && "Não foi possível aceitar o convite"}
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-5 px-4 pb-5 sm:px-7 sm:pb-7">
              {state === "loading" && (
                <div className="flex flex-col items-center gap-3 py-8">
                  <Loader2 className="h-10 w-10 animate-spin text-red-500" />
                  <p className="text-sm text-gray-500">
                    Aceitando convite...
                  </p>
                </div>
              )}

              {state === "success" && (
                <div className="flex flex-col items-center gap-3 py-8">
                  <div className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50">
                    <CheckCircle2 className="h-8 w-8 text-emerald-500" />
                  </div>
                  <p className="text-sm text-gray-600">
                    Você foi adicionado à organização. Redirecionando...
                  </p>
                </div>
              )}

              {state === "error" && (
                <div className="flex flex-col items-center gap-4 py-8">
                  <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-50">
                    <XCircle className="h-8 w-8 text-red-500" />
                  </div>
                  <p className="text-center text-sm text-gray-600">
                    {errorMessage}
                  </p>
                  <div className="flex gap-3">
                    <Button
                      asChild
                      variant="outline"
                      className="rounded-lg border-gray-200"
                    >
                      <Link to="/login">Fazer login</Link>
                    </Button>
                    <Button
                      asChild
                      className="rounded-lg border-0 bg-red-600 text-white hover:bg-red-700"
                    >
                      <Link to="/campaign">Ir para o painel</Link>
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <footer className="pb-4 text-center text-xs text-gray-500">
          &copy; {new Date().getFullYear()} Ad Pulse. Feito com{" "}
          <span className="text-red-500">&#10084;</span>
        </footer>
      </div>
    </React.Fragment>
  );
};

export default AcceptInvitation;
